import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as authApi from '../api/authApi';
import { clearSession } from '../../utils/storage';

const token = localStorage.getItem('rrc_token') || null;
const user = JSON.parse(localStorage.getItem('rrc_user') || 'null');

export const login = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const data = await authApi.login(credentials);
    localStorage.setItem('rrc_token', data.token);
    localStorage.setItem('rrc_user', JSON.stringify(data.user));
    return data;
  } catch (err) {
    return rejectWithValue(err.message || 'Login failed');
  }
});

export const register = createAsyncThunk('auth/register', async (userData, { rejectWithValue }) => {
  try {
    const data = await authApi.register(userData);
    localStorage.setItem('rrc_token', data.token);
    localStorage.setItem('rrc_user', JSON.stringify(data.user));
    return data;
  } catch (err) {
    return rejectWithValue(err.message || 'Registration failed');
  }
});

export const getMe = createAsyncThunk('auth/getMe', async (_, { rejectWithValue }) => {
  try {
    const data = await authApi.getMe();
    localStorage.setItem('rrc_user', JSON.stringify(data.user));
    return data;
  } catch (err) {
    return rejectWithValue(err.message || 'Failed to fetch profile');
  }
});

export const updateProfile = createAsyncThunk('auth/updateProfile', async (profileData, { rejectWithValue }) => {
  try {
    const data = await authApi.updateProfile(profileData);
    localStorage.setItem('rrc_user', JSON.stringify(data.user));
    return data;
  } catch (err) {
    return rejectWithValue(err.message || 'Update failed');
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  // Tell the server first so it clears the httpOnly cookie. Doing this only
  // client-side left a still-valid session behind after "logout" - reopening
  // the tab on a shared machine resumed the authenticated session.
  try {
    await authApi.logout();
  } catch {
    // Even if the request fails the local session must still be dropped.
  }
  clearSession();
  return null;
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: user,
    token: token,
    isAuthenticated: !!token,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getMe.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(getMe.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        clearSession();
      })
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
